import { money } from "./inbox-text.js";
/**
 * As frases de Cobranças (F135), lidas pelo site e pelo app do celular
 * (F310): as abas, os rótulos do contato, a linha do tempo e a mensagem do
 * WhatsApp. A data vem de fora (`formatDate`), porque o site usa o fuso do
 * navegador e o celular usa o da loja.
 */
export const COLLECTION_FILTER_LABELS = {
    overdue: 'Atrasados',
    promised_today: 'Promessa hoje',
    broken: 'Promessa quebrada',
    due_soon: 'Vence em 7 dias',
    all: 'Todos',
};
/** Como o contato aparece na linha do tempo. */
export const COLLECTION_CHANNEL_LABELS = {
    phone: 'Ligação',
    whatsapp: 'WhatsApp do celular',
    whatsapp_official: 'WhatsApp da loja',
    in_person: 'Pessoalmente',
    other: 'Contato',
};
/** O resultado, em minúsculas, para vir depois do canal: "Ligação · não atendeu". */
export const COLLECTION_OUTCOME_LABELS = {
    talked: 'conversou',
    no_answer: 'não atendeu',
    promised: 'prometeu pagar',
    refused: 'recusou pagar',
    wrong_number: 'número errado',
    message_sent: 'mensagem enviada',
    note: 'anotação',
};
/**
 * As escolhas do "Registrar contato". O WhatsApp da loja não está aqui: ele
 * se registra sozinho quando o modelo sai.
 */
export const COLLECTION_CONTACT_CHANNELS = [
    { value: 'phone', label: 'Ligação' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'in_person', label: 'Pessoalmente' },
    { value: 'other', label: 'Outro' },
];
/** "Mensagem enviada" também fica de fora: é o que o botão do WhatsApp grava. */
export const COLLECTION_CONTACT_OUTCOMES = [
    { value: 'promised', label: 'Prometeu pagar' },
    { value: 'talked', label: 'Conversei, sem data' },
    { value: 'no_answer', label: 'Não atendeu' },
    { value: 'refused', label: 'Recusou pagar' },
    { value: 'wrong_number', label: 'Número errado' },
    { value: 'note', label: 'Só uma anotação' },
];
const plural = (count, one, many) => `${String(count)} ${count === 1 ? one : many}`;
/** `2026-10-20` → `20/10/2026`. A promessa é um dia, não um instante. */
export const promiseDayLabel = (day) => {
    const [year, month, date] = day.split('-');
    return `${date ?? ''}/${month ?? ''}/${year ?? ''}`;
};
/** `2026-10-20` → `20/10`, para o selo da fila. */
export const promiseShortDayLabel = (day) => {
    const [, month, date] = day.split('-');
    return `${date ?? ''}/${month ?? ''}`;
};
/** Contatos, pagamentos e renegociações numa linha só, do mais novo ao mais antigo. */
export const collectionTimelineOf = (detail) => [
    ...detail.contacts.map((contact) => ({
        kind: 'contact',
        id: contact.id,
        at: contact.createdAt,
        contact,
    })),
    ...detail.settlements.map((settlement) => ({
        kind: 'settlement',
        id: settlement.id,
        at: settlement.createdAt,
        settlement,
    })),
    ...detail.renegotiations.map((renegotiation) => ({
        kind: 'renegotiation',
        id: renegotiation.id,
        at: renegotiation.createdAt,
        renegotiation,
    })),
].sort((left, right) => right.at.localeCompare(left.at));
/** O que aconteceu (`title`) e quem/sobre o quê (`detail`), como o site escreve. */
export const collectionTimelineText = (item) => {
    if (item.kind === 'contact') {
        const { contact } = item;
        return {
            title: contact.outcome === 'promised'
                ? `${COLLECTION_CHANNEL_LABELS[contact.channel]} · prometeu ${money(contact.promisedAmount ?? '0')} para ${promiseDayLabel(contact.promisedFor ?? '')}`
                : `${COLLECTION_CHANNEL_LABELS[contact.channel]} · ${COLLECTION_OUTCOME_LABELS[contact.outcome]}`,
            detail: [contact.actorName, contact.note]
                .filter((part) => part !== null && part !== '')
                .join(' · '),
        };
    }
    if (item.kind === 'settlement') {
        const { settlement } = item;
        return {
            title: settlement.isReversal
                ? `Estorno de ${money(settlement.paidAmount)}`
                : `Pagou ${money(settlement.paidAmount)}${settlement.methodName === null ? '' : ` em ${settlement.methodName}`}`,
            detail: [
                settlement.entryDescription,
                settlement.actorName,
                settlement.reversed ? 'estornado' : null,
            ]
                .filter((part) => part !== null)
                .join(' · '),
        };
    }
    const { renegotiation } = item;
    return {
        title: `Renegociou ${money(renegotiation.originalTotal)} em ${String(renegotiation.instalmentCount)}x (${money(renegotiation.newTotal)})`,
        detail: [
            plural(renegotiation.replacedCount, 'título', 'títulos'),
            renegotiation.actorName,
            renegotiation.reason,
        ]
            .filter((part) => part !== null)
            .join(' · '),
    };
};
/** A faixa da promessa vigente: o título e a frase de baixo. */
export const collectionPromiseText = (promise) => ({
    title: promise.state === 'broken'
        ? `Promessa quebrada: ${money(promise.amount)} para ${promiseDayLabel(promise.promisedFor)}`
        : promise.state === 'due_today'
            ? `Prometeu pagar ${money(promise.amount)} hoje`
            : `Prometeu pagar ${money(promise.amount)} até ${promiseDayLabel(promise.promisedFor)}`,
    detail: promise.paidSince === '0.00'
        ? 'Nada pago desde a promessa.'
        : `Pagou ${money(promise.paidSince)} desde a promessa.`,
});
const LISTED = 5;
const firstName = (name) => {
    const first = name.trim().split(/\s+/)[0] ?? '';
    return first.length === 0 ? name : first;
};
/**
 * O texto da cobrança pelo WhatsApp do celular (F135).
 *
 * Educado e específico: o nome, a loja, quanto está em aberto e o que venceu.
 * "Consta um débito" sem valor faz o cliente responder "quanto?", e a
 * conversa começa com uma pergunta em vez de um combinado.
 *
 * Até cinco títulos listados: uma mensagem com vinte linhas de vencimento não é
 * lida, e o extrato impresso existe para quem precisa da lista inteira.
 */
export const collectionMessage = (input) => {
    const overdue = input.entries.filter((entry) => entry.daysOverdue > 0);
    const listed = (overdue.length > 0 ? overdue : input.entries).slice(0, LISTED);
    const more = (overdue.length > 0 ? overdue.length : input.entries.length) - listed.length;
    const lines = listed.map((entry) => `• ${money(entry.outstanding)} — ${entry.daysOverdue > 0 ? 'venceu' : 'vence'} em ${input.formatDate(entry.dueAt)}`);
    if (more > 0) {
        lines.push(`• e mais ${String(more)} ${more === 1 ? 'parcela' : 'parcelas'}`);
    }
    return [
        `Olá, ${firstName(input.customerName)}! Aqui é da ${input.companyName}.`,
        '',
        overdue.length > 0
            ? `Consta em aberto no seu cadastro ${money(input.total)}:`
            : `Passando para lembrar do seu próximo vencimento (${money(input.total)} em aberto):`,
        ...lines,
        '',
        'Podemos combinar o pagamento? Se já pagou, desconsidere esta mensagem.',
    ].join('\n');
};
//# sourceMappingURL=collections.js.map